import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Persist session id per user in localStorage; a fresh session can be started with "Clear"
const SESSION_KEY = "woodland_ai_session_id";

function newSessionId() {
  return "sess-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = newSessionId();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// Try to extract project id from current URL path (e.g. /projects/<id>)
function projectIdFromPath(pathname) {
  const m = pathname.match(/\/projects\/([a-f0-9-]{8,})/i);
  return m ? m[1] : null;
}

export default function AskAIDrawer({ open, onClose }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(getSessionId);
  const [projectId, setProjectId] = useState(projectIdFromPath(loc.pathname));
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Keep project scope in sync with URL
  useEffect(() => {
    setProjectId(projectIdFromPath(loc.pathname));
  }, [loc.pathname]);

  // Load prior session history on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/ai/sessions/${sessionId}`);
        setMessages(data.messages || []);
      } catch { /* new session */ }
    })();
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/timeline", {
        session_id: sessionId,
        project_id: projectId || null,
        message: q,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, usage: data.usage }]);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "AI request failed";
      setMessages((m) => [...m, { role: "error", content: typeof msg === "string" ? msg : JSON.stringify(msg) }]);
    } finally { setBusy(false); }
  };

  const clearChat = async () => {
    try { await api.delete(`/ai/sessions/${sessionId}`); } catch (_) { /* ignore */ }
    const sid = newSessionId();
    localStorage.setItem(SESSION_KEY, sid);
    setSessionId(sid);
    setMessages([]);
    toast.success("New chat started");
  };

  const suggestions = projectId ? [
    "Which tasks are running late in this project?",
    "Who is overloaded next week?",
    "Summarise the critical path for this project.",
    "How many milestones are still open?",
  ] : [
    "Which projects have late tasks right now?",
    "Which project has the most open work?",
    "Give me a portfolio status overview.",
    "Who has the most open tasks assigned?",
  ];

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" onClick={onClose} data-testid="askai-backdrop" />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Ask AI"
        data-testid="askai-drawer"
        className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[440px] bg-card border-l border-border/60 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
      >
        {/* Header */}
        <div className="h-14 flex items-center gap-2 px-4 border-b border-border/60">
          <Sparkles className="w-4 h-4 text-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Ask AI</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {projectId ? "Project-scoped answers" : "Portfolio-wide answers"} · Gemma 4 31B
            </div>
          </div>
          <button onClick={clearChat} title="Start a new chat" data-testid="askai-clear"
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"><Trash2 className="w-4 h-4" /></button>
          <button onClick={onClose} data-testid="askai-close" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="askai-messages">
          {messages.length === 0 && (
            <div className="text-center py-8 space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium">Ask about your projects</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {projectId ? "This conversation is scoped to the project you're viewing." : "Navigate into a project to scope answers, or ask portfolio-wide questions here."}
                </div>
              </div>
              <div className="grid gap-1.5 pt-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setText(s)}
                    data-testid={`askai-suggest-${i}`}
                    className="text-left text-xs rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/40 px-3 py-2 transition"
                  >
                    <span className="text-muted-foreground">→</span> {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`askai-msg-${i}`}
              className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user"       ? "bg-primary/10 text-foreground ml-6 rounded-tr-none border border-primary/20" :
                m.role === "error"      ? "bg-rose-500/10 text-rose-500 mr-6 rounded-tl-none border border-rose-500/30" :
                                          "bg-muted/40 text-foreground mr-6 rounded-tl-none border border-border/60"
              }`}
            >
              {m.content}
              {m.usage && (
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">
                  {m.usage.total_tokens} tokens
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="rounded-lg px-3 py-2 text-sm bg-muted/40 mr-6 rounded-tl-none border border-border/60 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span className="text-xs text-muted-foreground">Thinking…</span>
            </div>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-border/60 p-3 flex gap-2 items-end bg-muted/10">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            placeholder="Ask about tasks, workload, milestones…"
            rows={2}
            data-testid="askai-input"
            className="flex-1 resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          <Button type="submit" disabled={busy || !text.trim()} size="sm" data-testid="askai-send"
            className="bg-primary text-primary-foreground shrink-0 h-10 w-10 p-0">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </aside>
    </>
  );
}
